import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { ReactComponent as ProductIcon } from 'assets/icons/product.svg';
import { ReactComponent as WarehouseIcon } from 'assets/icons/warehouse.svg';
import clsx from 'clsx';
import { NO_LOCATION_KEY } from 'constants/general';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { compareCountryGroupKeys, createCountryLookup } from 'utils/country';

import type { TCountryLookup } from 'utils/country';
import type IScenarioItem from './interfaces/IScenarioItem';

import styles from './sass/ScenarioItem.module.scss';

function ScenarioItem({ scenario }: IScenarioItem) {
  const { t } = useTranslation();
  const [isEntitiesExpanded, setIsEntitiesExpanded] = useState(false);
  type TCountryGroup = { label: string, units: IScenarioItem['scenario']['unitsSnapshot'] };

  const { data: locationsCountries } = useGetLocationsCountriesQuery();
  const countryLookup = useMemo(
    () => createCountryLookup(locationsCountries?.data),
    [locationsCountries],
  );

  const resolveCountryName = (
    countryCode: string | undefined,
    country: string | undefined,
    lookup: TCountryLookup,
  ): string | null => {
    const candidates = [countryCode, country].filter((v): v is string => Boolean(v));

    const resolvedCandidate = candidates.find((candidate) => {
      const normalized = candidate.trim().toLowerCase();
      const alias = normalized.replace(/[^a-z0-9]/g, '');
      return lookup.get(normalized) || lookup.get(alias);
    });

    if (!resolvedCandidate) return null;

    const normalized = resolvedCandidate.trim().toLowerCase();
    const alias = normalized.replace(/[^a-z0-9]/g, '');
    return lookup.get(normalized) || lookup.get(alias) || null;
  };

  const groupedUnits = useMemo(() => {
    const groups = new Map<string, TCountryGroup>();

    scenario.unitsSnapshot.forEach((unit) => {
      const resolvedName = resolveCountryName(unit.countryCode, unit.country, countryLookup);
      const key = resolvedName ?? NO_LOCATION_KEY;
      const label = resolvedName ?? '';
      if (!groups.has(key)) groups.set(key, { label, units: [] });
      groups.get(key)!.units.push(unit);
    });

    return Array.from(groups.entries())
      .sort(([leftKey], [rightKey]) => compareCountryGroupKeys(leftKey, rightKey))
      .map(([key, group]) => ({ key, ...group }));
  }, [scenario, countryLookup]);

  const geography = useMemo(() => (
    groupedUnits
      .filter(({ key }) => key !== NO_LOCATION_KEY)
      .map(({ label }) => label)
  ), [groupedUnits]);

  const formatPeriod = (): string | null => {
    if (!scenario.affectedPeriod || !scenario.affectedPeriodUnit) return null;
    const count = scenario.affectedPeriod;
    const unit = scenario.affectedPeriodUnit;
    return `${count} ${unit}${count !== 1 ? 's' : ''}`;
  };

  const formatImpact = (): string | null => {
    if (scenario.productivityImpact === undefined || scenario.productivityImpact === null) {
      return null;
    }
    const value = scenario.productivityImpact;
    return `${value > 0 ? '+' : ''}${value}%`;
  };

  const period = formatPeriod();
  const impact = formatImpact();
  const hasInfo = period || impact || geography.length > 0;

  return (
    <div className={styles.scenarioCard}>
      <div className={styles.scenarioHeader}>
        <span className={styles.scenarioName}>{scenario.name}</span>
      </div>

      {hasInfo ? (
        <div className={styles.scenarioInfo}>
          {period ? (
            <div className={styles.scenarioInfoRow}>
              <span className={styles.scenarioInfoLabel}>
                {t('valueChainMap.affectedPeriodLabel')}
                :
              </span>
              <span className={styles.scenarioInfoValue}>{period}</span>
            </div>
          ) : null}
          {impact ? (
            <div className={styles.scenarioInfoRow}>
              <span className={styles.scenarioInfoLabel}>
                {t('valueChainMap.scenarioProductivityImpact')}
                :
              </span>
              <span className={styles.scenarioInfoValue}>{impact}</span>
            </div>
          ) : null}
          {geography.length > 0 ? (
            <div className={styles.scenarioInfoRow}>
              <span className={styles.scenarioInfoLabel}>
                {t('valueChainMap.affectedGeography')}
                :
              </span>
              <span className={styles.scenarioInfoValue}>{geography.join(', ')}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {scenario.unitsSnapshot.length > 0 ? (
        <div className={styles.scenarioEntities}>
          <button
            type="button"
            className={clsx(styles.scenarioEntitiesHeader, {
              [styles.scenarioEntitiesHeaderExpanded]: isEntitiesExpanded,
            })}
            onClick={() => setIsEntitiesExpanded((prev) => !prev)}
            aria-expanded={isEntitiesExpanded}
          >
            <span className={styles.scenarioEntitiesTitle}>
              {t('valueChainMap.affectedEntitiesLabel')}
              {' '}
              <span className={styles.scenarioEntitiesCount}>
                (
                {scenario.unitsSnapshot.length}
                )
              </span>
            </span>
            <ChevronIcon
              className={clsx(styles.scenarioChevron, {
                [styles.scenarioChevronExpanded]: isEntitiesExpanded,
              })}
              aria-hidden="true"
            />
          </button>

          {isEntitiesExpanded ? (
            <div className={styles.scenarioEntitiesList}>
              {groupedUnits.map(({ key, label, units }) => {
                const groupLabel = key === NO_LOCATION_KEY
                  ? `${t('valueChainMap.noLocation')} (${units.length}):`
                  : `${label} (${units.length}):`;

                return (
                  <div key={key} className={styles.scenarioCountryGroup}>
                    <span className={styles.scenarioCountryLabel}>{groupLabel}</span>
                    <div className={styles.scenarioChips}>
                      {units.map((unit) => (
                        <div
                          key={unit.id}
                          className={styles.scenarioChip}
                          data-type={unit.type}
                        >
                          <span className={styles.scenarioChipIcon}>
                            {unit.type === VALUE_CHAIN_UNIT_TYPES.SUPPLIER ? <WarehouseIcon /> : <ProductIcon />}
                          </span>
                          <span className={styles.scenarioChipName}>{unit.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default ScenarioItem;
